import { apiUrl } from '@/config/runtime.js'

export class CloudApiError extends Error {
  constructor(message, { status = 0, code = 'CLOUD_REQUEST_FAILED', diagnosticId = '', details = null } = {}) {
    super(message)
    this.name = 'CloudApiError'
    this.status = status
    this.code = code
    this.diagnosticId = diagnosticId
    this.details = details
  }
}

let refreshSessionRequest = null
let expireSessionRequest = null
const WORKSPACE_CSRF_COOKIES = Object.freeze({
  superadmin: 'pm_sa_csrf',
  enterprise_admin: 'pm_ea_csrf',
  project_manager: 'pm_pm_csrf',
})

export function workspaceTypeForPath(pathname = '') {
  const path = String(pathname || '')
  if (path.startsWith('/superadmin') || path.startsWith('/login/superadmin')) return 'superadmin'
  if (path.startsWith('/enterprise-admin') || path.startsWith('/login/enterprise-admin')) return 'enterprise_admin'
  return 'project_manager'
}

export function currentWorkspaceType() {
  return workspaceTypeForPath(typeof window === 'undefined' ? '' : window.location.pathname)
}

function isAuthEndpoint(pathname) {
  return ['/api/web/auth/login', '/api/web/auth/refresh', '/api/web/auth/logout'].includes(pathname)
}

function notifySessionExpired() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('pm:session-expired'))
    if (!window.location.pathname.startsWith('/login')) {
      const next = `${window.location.pathname}${window.location.search}${window.location.hash}`
      const loginPath = window.location.pathname.startsWith('/superadmin')
        ? '/login/superadmin'
        : window.location.pathname.startsWith('/enterprise-admin')
          ? '/login/enterprise-admin'
          : '/login'
      window.location.assign(`${loginPath}?next=${encodeURIComponent(next)}`)
    }
  }
}

async function expireWebSession(timeoutMs, workspaceType) {
  if (!expireSessionRequest) {
    expireSessionRequest = cloudFetch('/api/web/auth/logout', {
      method: 'POST',
      timeoutMs,
      workspaceType,
      refreshOnUnauthorized: false,
    })
      .catch(() => null)
      .finally(() => {
        notifySessionExpired()
        expireSessionRequest = null
      })
  }
  return expireSessionRequest
}

async function refreshSession(timeoutMs, workspaceType) {
  if (!refreshSessionRequest) {
    refreshSessionRequest = cloudFetch('/api/web/auth/refresh', {
      method: 'POST',
      timeoutMs,
      workspaceType,
      refreshOnUnauthorized: false,
    }).finally(() => {
      refreshSessionRequest = null
    })
  }
  return refreshSessionRequest
}

function mergeSignals(signals) {
  const available = signals.filter(Boolean)
  const unchanged = signal => ({ signal, dispose() {} })
  if (available.length < 2) return unchanged(available[0])
  if (typeof AbortSignal.any === 'function') return unchanged(AbortSignal.any(available))
  const controller = new AbortController()
  const listeners = []
  const dispose = () => {
    for (const [source, listener] of listeners) source.removeEventListener('abort', listener)
    listeners.length = 0
  }
  const abort = source => { controller.abort(source.reason); dispose() }
  const aborted = available.find(source => source.aborted)
  if (aborted) abort(aborted)
  else {
    try {
      for (const source of available) {
        const listener = () => abort(source)
        listeners.push([source, listener])
        source.addEventListener('abort', listener, { once: true })
      }
    } catch (error) { dispose(); throw error }
  }
  return { signal: controller.signal, dispose }
}

function requestId() {
  return globalThis.crypto?.randomUUID?.() || `web-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function cookieValue(name) {
  if (typeof document === 'undefined') return ''
  const prefix = `${encodeURIComponent(name)}=`
  const item = document.cookie.split('; ').find((value) => value.startsWith(prefix))
  return item ? decodeURIComponent(item.slice(prefix.length)) : ''
}

export function hasWebSessionHint(workspaceType = currentWorkspaceType()) {
  return Boolean(cookieValue(WORKSPACE_CSRF_COOKIES[workspaceType] || WORKSPACE_CSRF_COOKIES.project_manager))
}

const FIELD_LABELS = Object.freeze({
  new_password: '新密码',
  initial_password: '初始密码',
  reason: '重置原因',
  email: '登录邮箱',
  display_name: '账号名',
  tenant_name: '企业名称',
})

function validationMessage(detail) {
  if (!Array.isArray(detail)) return ''
  return detail.map((item) => {
    const field = Array.isArray(item?.loc) ? item.loc.at(-1) : ''
    const label = FIELD_LABELS[field] || field || '表单字段'
    const minimum = item?.ctx?.min_length
    if (minimum != null) return `${label}至少需要 ${minimum} 个字符`
    if (item?.type === 'missing') return `请填写${label}`
    return `${label}：${item?.msg || '输入内容不符合要求'}`
  }).join('；')
}

async function errorFromResponse(response) {
  let payload = null
  try {
    payload = await response.clone().json()
  } catch {
    payload = null
  }
  const detail = payload?.detail
  const message = typeof detail === 'string'
    ? detail
    : validationMessage(detail) || detail?.message || payload?.message || `云端请求失败（${response.status}）`
  return new CloudApiError(message, {
    status: response.status,
    code: detail?.code || payload?.code || `HTTP_${response.status}`,
    diagnosticId: response.headers.get('x-diagnostic-id') || response.headers.get('x-request-id') || payload?.diagnostic_id || '',
    details: detail || payload,
  })
}

export async function cloudFetch(pathname, options = {}) {
  const {
    timeoutMs = 30_000,
    signal,
    headers,
    body,
    method = 'GET',
    parseAs = 'json',
    idempotencyKey,
    expectedRevision,
    workspaceType = currentWorkspaceType(),
    refreshOnUnauthorized = true,
    retryCount = 0,
    maxReadRetries = 2,
    ...fetchOptions
  } = options
  const timeoutController = new AbortController()
  const timeout = setTimeout(() => timeoutController.abort(new DOMException('cloud request timeout', 'TimeoutError')), timeoutMs)
  const requestHeaders = new Headers(headers || {})
  if (!requestHeaders.has('Accept')) requestHeaders.set('Accept', parseAs === 'blob' ? '*/*' : 'application/json')
  requestHeaders.set('X-Client-Request-ID', requestId())
  requestHeaders.set('X-Workspace-Type', workspaceType)
  if (idempotencyKey) requestHeaders.set('Idempotency-Key', idempotencyKey)
  if (expectedRevision !== undefined && expectedRevision !== null) {
    requestHeaders.set('If-Match', String(expectedRevision))
  }
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase())) {
    const csrfToken = cookieValue(WORKSPACE_CSRF_COOKIES[workspaceType] || WORKSPACE_CSRF_COOKIES.project_manager)
    if (csrfToken) requestHeaders.set('X-CSRF-Token', csrfToken)
  }
  if (body && !(body instanceof FormData) && !requestHeaders.has('Content-Type')) {
    requestHeaders.set('Content-Type', 'application/json')
  }

  let disposeSignals = () => {}
  let requestStarted = false
  try {
    const combined = mergeSignals([signal, timeoutController.signal])
    disposeSignals = combined.dispose
    requestStarted = true
    const response = await fetch(apiUrl(pathname), {
      ...fetchOptions,
      method,
      credentials: 'include',
      cache: method === 'GET' ? 'no-store' : 'default',
      headers: requestHeaders,
      body: body && !(body instanceof FormData) && typeof body !== 'string' ? JSON.stringify(body) : body,
      signal: combined.signal,
    })
    if (
      response.status === 401
      && refreshOnUnauthorized
      && !isAuthEndpoint(pathname)
    ) {
      try {
        await refreshSession(timeoutMs, workspaceType)
      } catch (refreshError) {
        if (refreshError instanceof CloudApiError && [401, 403].includes(refreshError.status)) {
          await expireWebSession(timeoutMs, workspaceType)
        }
        throw refreshError
      }
      return cloudFetch(pathname, {
        ...options,
        workspaceType,
        refreshOnUnauthorized: false,
      })
    }
    if (!response.ok) throw await errorFromResponse(response)
    if (parseAs === 'response') return response
    if (parseAs === 'blob') return await response.blob()
    if (parseAs === 'text') return await response.text()
    if (response.status === 204) return null
    return await response.json()
  } catch (error) {
    if (signal?.aborted) throw error
    if (!requestStarted) {
      throw new CloudApiError('当前浏览器暂时无法准备请求，请重新打开页面后重试。', {
        code: 'CLOUD_REQUEST_SETUP_ERROR', details: { cause: error?.name || 'Error' },
      })
    }
    if (error instanceof SyntaxError) {
      throw new CloudApiError('云端返回的数据暂时无法读取，已保存的内容不受影响。', {
        code: 'CLOUD_INVALID_RESPONSE',
      })
    }
    const readMethod = ['GET', 'HEAD'].includes(method.toUpperCase())
    const retryableStatus = error instanceof CloudApiError && [408, 425, 429, 500, 502, 503, 504].includes(error.status)
    const retryableConnection = !(error instanceof CloudApiError) && !signal?.aborted
    if (readMethod && retryCount < maxReadRetries && (retryableStatus || retryableConnection || timeoutController.signal.aborted)) {
      clearTimeout(timeout)
      await new Promise((resolve) => setTimeout(resolve, 250 * (2 ** retryCount)))
      if (signal?.aborted) throw signal.reason || error
      return cloudFetch(pathname, { ...options, method, workspaceType, retryCount: retryCount + 1, maxReadRetries })
    }
    if (error instanceof CloudApiError) throw error
    if (timeoutController.signal.aborted) {
      throw new CloudApiError('云端暂时没有响应，软件会保留当前任务状态。', {
        code: 'CLOUD_REQUEST_TIMEOUT',
      })
    }
    if (signal?.aborted) throw error
    throw new CloudApiError('暂时无法连接云端，请稍后自动重试。', {
      code: 'CLOUD_CONNECTION_ERROR',
      details: { cause: error?.name || 'Error' },
    })
  } finally {
    clearTimeout(timeout)
    disposeSignals()
  }
}

export const cloudApi = Object.freeze({
  get: (path, options) => cloudFetch(path, { ...options, method: 'GET' }),
  post: (path, body, options) => cloudFetch(path, { ...options, method: 'POST', body }),
  put: (path, body, options) => cloudFetch(path, { ...options, method: 'PUT', body }),
  patch: (path, body, options) => cloudFetch(path, { ...options, method: 'PATCH', body }),
  delete: (path, options) => cloudFetch(path, { ...options, method: 'DELETE' }),
})
